import { useState, useEffect, useRef } from "react";
import { Icons } from "assets";
import styles from "./style.module.scss";
import classNames from "classnames";
import Spinner from "../spinner";

interface OptionProps {
  id: number;
  title: string;
  action?: () => void;
}

interface CustomSelectProps {
  label?: string;
  selectedItem?: string | any;
  required?: boolean;
  options: OptionProps[];
  defaultActiveItem?: string;
  customDropdownMenu?: string;
  customSortStyle?: string;
  enableIconTrigger?: boolean;
  customSortLabelStyle?: string;
  onChange?: (selected: OptionProps) => void | any;
  error?: string;
  customSelectWrapper?: string;
  emptyPlaceholder?: string;
  loading?: boolean;
}

const CustomSelect = ({
  options,
  label,
  required,
  customSortStyle,
  customSortLabelStyle,
  customDropdownMenu,
  defaultActiveItem = "Choose an option",
  enableIconTrigger = false,
  onChange,
  error,
  customSelectWrapper,
  emptyPlaceholder = "No options available",
  loading = false,
  selectedItem,
}: CustomSelectProps) => {
  const [openSelection, setOpenSelection] = useState(false);
  const [activeItem, setActiveItem] = useState(() => {
    if (selectedItem) {
      if (typeof selectedItem === "string") {
        return selectedItem;
      }
      if (selectedItem?.title) {
        return selectedItem.title;
      }
    }
    return defaultActiveItem;
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedItem) {
      if (typeof selectedItem === "string") {
        setActiveItem(selectedItem);
      } else if (selectedItem?.title) {
        setActiveItem(selectedItem.title);
      }
    } else {
      setActiveItem(defaultActiveItem);
    }
  }, [selectedItem, defaultActiveItem]);

  const handleOptionClick = (option: OptionProps) => {
    setActiveItem(option.title);
    if (onChange) {
      onChange(option);
    }
    setOpenSelection(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenSelection(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div
      className={classNames(
        styles.selectWrapper,
        customSelectWrapper,
        "flex flex-col gap-2 items-start justify-center",
      )}
    >
      <div
        ref={dropdownRef}
        className={classNames(
          styles.sortContainer,
          customSortStyle && customSortStyle,
          "w-full",
        )}
      >
        {label && (
          <label className={styles.inputLabel}>
            {label}{" "}
            {!!required && (
              <label className={classNames(styles.asterik)}>*</label>
            )}
          </label>
        )}
        <div
          className={classNames(
            styles.sortLabel,
            customSortLabelStyle,
            !!error && styles.sortLabelError,
            "flex items-center justify-between cursor-pointer w-full",
          )}
          onClick={() => setOpenSelection(!openSelection)}
        >
          <div className="flex items-center justify-between w-full gap-2">
            {enableIconTrigger ? (
              <div className={classNames(styles.triggerIcon)}>
                <Icons.ProjectsIcon />
              </div>
            ) : (
              <></>
            )}
            <div className="flex justify-between gap-12 w-full">
              <span className={classNames(styles.activeItem)}>
                {activeItem}
              </span>
              <Icons.ChevDown className="flex ml-auto justify-end" />
            </div>
          </div>
        </div>

        {openSelection && (
          <div className={classNames(styles.dropdown, customDropdownMenu)}>
            {loading ? (
              <div
                className={classNames(
                  styles.optionItem,
                  "flex justify-center items-center",
                )}
                style={{ cursor: "default" }}
              >
                <Spinner />
              </div>
            ) : options.length > 0 ? (
              options.map((option) => (
                <div
                  key={option.id}
                  className={classNames(styles.optionItem, {
                    [styles.active]: option.title === activeItem,
                  })}
                  onClick={() => handleOptionClick(option)}
                >
                  <p>{option.title}</p>
                </div>
              ))
            ) : (
              <div className={classNames(styles.optionItem)}>
                <p style={{ opacity: 0.6, cursor: "default" }}>
                  {emptyPlaceholder}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {!!error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default CustomSelect;
